import { useEffect, useState } from 'react';
import { BasicSelectProps, SelectExtendedProps } from 'grommet';
import { observer } from 'mobx-react-lite';
import { ApiError, ProgramService } from '/src/api';
import { BoxProps, Select } from '/src/components';
import { useGlobalStore, useUserStore } from '/src/context';
import { Program, TCountryId, TProgramId } from '/src/lib/models';
import { toastMessages } from '/src/lib/toast';
import { Nullable, SelectOption } from '/src/lib/types';

export type ProgramSelectProps = {
  value?: Nullable<number>;
  setValue?: (index: number) => void;
  multiple?: boolean;
  selectedProgramIds?: TProgramId[];
  setSelectedProgramIds?: (ids: TProgramId[]) => void;
  programs?: Program[];
  error?: string;
  required?: boolean;
  countryId?: Nullable<TCountryId>;
  hideOptionalText?: boolean;
  hideLabel?: boolean;
  emptySearchMessage?: string;
  searchPlaceholder?: string;
  style?: SelectExtendedProps['style'];
  fill?: BoxProps['fill'];
  width?: BoxProps['width'];
  label?: string;
  placeholder?: string;
  id?: string;
  name?: string;
  disabled?: BasicSelectProps['disabled'];
  onSubmit?: (event: React.KeyboardEvent<HTMLElement>) => void;
  filterOutOptions?: TProgramId[];
  options?: SelectOption<TProgramId>[];
  hideSelectAll?: boolean;
};

export const ProgramSelect: React.FC<ProgramSelectProps> = observer((props) => {
  const {
    value,
    setValue,
    multiple,
    selectedProgramIds,
    setSelectedProgramIds,
    programs,
    error,
    required,
    fill,
    width,
    hideOptionalText,
    hideLabel,
    emptySearchMessage,
    searchPlaceholder,
    label,
    placeholder,
    countryId,
    id,
    name,
    disabled,
    onSubmit,
    filterOutOptions,
    options,
    hideSelectAll,
  } = props;

  /** Stores **/
  const globalStore = useGlobalStore();
  const userStore = useUserStore();

  /** State **/
  const [isLoadingPrograms, setIsLoadingPrograms] = useState(true);
  const [programsList, setProgramsList] = useState<Program[]>([]);
  const [defaultSelectOptions, setDefaultSelectOptions] = useState<SelectOption<TProgramId>[]>([]);
  const [selectOptions, setSelectOptions] = useState<SelectOption<TProgramId>[]>([]);

  /** Methods **/
  const fetchProgramsList = async (country_id: TCountryId) => {
    if (!userStore.user) return;

    try {
      setIsLoadingPrograms(true);

      const programsList = programs || (await ProgramService.list({ country_id }));
      setProgramsList(programsList);

      let options = programsList.map((program) => ({
        label: program.name,
        value: program.id,
      }));

      if (filterOutOptions) {
        options = options.filter(({ value }) => !filterOutOptions.includes(value));
      }

      setSelectOptions(options);
      setDefaultSelectOptions(options);
    } catch (err) {
      globalStore.handleApiError(err as ApiError, toastMessages.listPrograms.error);
    } finally {
      setIsLoadingPrograms(false);
    }
  };

  const filterPrograms = (query: string) => {
    if (query === '') setSelectOptions(defaultSelectOptions);
    const exp = new RegExp(query.replace(/[-\\^$*+?.()|[\]{}]/g, '\\$&'), 'i');
    const filteredOptions = defaultSelectOptions.filter(({ label }) => exp.test(label));
    setSelectOptions(filteredOptions);
  };

  /** Effects **/
  useEffect(() => {
    if (isLoadingPrograms) {
      if (!programsList.length) {
        if (programs) {
          setProgramsList(programs);
          const options = programs.map((program) => ({
            label: program.name,
            value: program.id,
          }));
          setSelectOptions(options);
          setDefaultSelectOptions(options);
        } else if (countryId) {
          fetchProgramsList(countryId);
        }
      } else {
        setIsLoadingPrograms(false);
      }
    }
  }, [programs, programsList, countryId, isLoadingPrograms, value]);

  useEffect(() => {
    if (countryId) {
      if (multiple) {
        setSelectedProgramIds?.([]);
      } else {
        setValue?.(NaN);
      }
      fetchProgramsList(countryId);
    }
  }, [countryId]);

  return (
    <Select
      multiple={multiple}
      id={id}
      name={id ? undefined : name || 'selected_program'}
      label={label ?? 'Program'}
      placeholder={placeholder}
      hideLabel={hideLabel}
      options={options ?? selectOptions}
      onOpen={() => filterPrograms('')}
      value={!isLoadingPrograms ? value : ''}
      values={selectedProgramIds ?? []}
      fill={fill}
      width={width}
      setValue={(value) => setValue?.(value)}
      setValues={(values) => setSelectedProgramIds?.(values as TProgramId[])}
      error={error}
      required={required}
      disabled={disabled}
      onSearch={selectOptions.length > 1 ? (query) => filterPrograms(query) : undefined}
      emptySearchMessage={emptySearchMessage}
      searchPlaceholder={searchPlaceholder}
      onSubmit={onSubmit}
      hideOptionalText={hideOptionalText}
      hideSelectAll={hideSelectAll}
    />
  );
});
